import { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import Loader from "../../components/Loader";
import ErrorBox from "../../components/ErrorBox";
import Dropdown from "../../components/Dropdown";
import HotdropdownV2 from "../../components/HotdropdownV2";
import { getAllEducationLevels } from "../../services/educationLevelServices";
// ASSETS
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import { createProduct } from "../../services/productServices";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";

const AddBookModal = ({ isOpen, onClose, onBookCreated, setPagination }) => {
  const { t } = useTranslation("general");

  const [loadingModal, setLoadingModal] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [message, setMessage] = useState("");
  const [academicYears, setAcademicYears] = useState([]);

  const [newBookFields, setNewBookFields] = useState({
    bookName: "",
    bookType: {
      title: "",
      value: "",
    },
    quantity: "",
    academicYear: {
      title: "",
      value: "",
      price: 0,
    },
    price: "",
    purchasePrice: "",
    notes: "",
    description: "",
  });

  const bookTypes = [
    { title: t("governmental"), value: "governmental" },
    { title: t("enrichment"), value: "enrichment" },
  ];

  const fetchAcademicYears = async (
    page = 1,
    limit = 5,
    isDropDown = false,
    searchTerm
  ) => {
    try {
      const response = await getAllEducationLevels({
        page,
        limit,
        search: searchTerm,
      });

      const transformedData = (response.data.classes || []).map((level) => ({
        title: level.name,
        value: level.id,
        price: level.price,
      }));

      if (page === 1) {
        setAcademicYears(transformedData);
      } else {
        setAcademicYears((prev) => [...prev, ...transformedData]);
      }

      return {
        status: response.status || 200,
        data: {
          classes: transformedData,
          totalPages: response.data.totalPages,
          totalRecords: response.data.totalRecords,
        },
      };
    } catch (error) {
      console.error("Error fetching academic years:", error);
      setAcademicYears([]);
      return {
        status: 500,
        data: { classes: [], totalPages: 1, totalRecords: 0 },
      };
    }
  };

  const handleNewBookInputChange = (e, fieldName) => {
    let value = e.target.value;
    setNewBookFields((prevFields) => ({
      ...prevFields,
      [fieldName]: value,
    }));
  };

  const handleCreateBook = async (e) => {
    e.preventDefault();
    const organizationId = localStorage.getItem("organizationId");

    try {
      // Required fields validation
      if (
        !newBookFields.bookName ||
        !newBookFields.bookType.value ||
        !newBookFields.quantity ||
        !newBookFields.academicYear.value ||
        !newBookFields.price ||
        !newBookFields.purchasePrice
      ) {
        setError(true);
        setErrorMessage(t("emptyInputFields"));
        return;
      }

      const newBookData = {
        name: newBookFields.bookName,
        productType: "book",
        productSubType: newBookFields.bookType.value,
        amountInStock: parseInt(newBookFields.quantity),
        schoolClassId: newBookFields.academicYear.value,
        price: parseFloat(newBookFields.price),
        purchasePrice: parseFloat(newBookFields.purchasePrice),
        notes: newBookFields.notes || null,
        organizationId: organizationId,
        date: new Date(),
        description: newBookFields.description || null,
      };

      setLoadingModal(true);

      // ✅ pass the data to the service
      const response = await createProduct(newBookData);

      setMessage(response.data.message);

      if (setPagination) {
        setPagination((prev) => ({
          ...prev,
          totalRecords: prev.totalRecords + 1,
        }));
      }

      if (response.status === 201) {
        setError(false);
        setErrorMessage("");

        // Reset form
        setNewBookFields({
          bookName: "",
          bookType: { title: "", value: "" },
          quantity: "",
          academicYear: { title: "", value: "", price: 0 },
          price: "",
          purchasePrice: "",
          notes: "",
          description: "",
        });

        // Call parent callback to refresh data
        onClose();
        if (onBookCreated) {
          onBookCreated();
        }
      }
    } catch (error) {
      if (error.response?.data?.error) {
        setError(true);
        setErrorMessage(error.response.data.error);
      } else {
        console.error("Network or other error:", error.message);
        setError(true);
        setErrorMessage("An unexpected error occurred");
      }
    } finally {
      setLoadingModal(false);
    }
  };

  const handleCancelAddBook = () => {
    setError(false);
    setErrorMessage("");

    // Reset form
    setNewBookFields({
      bookName: "",
      bookType: {
        title: "",
        value: "",
      },
      quantity: "",
      academicYear: {
        title: "",
        value: "",
        price: 0,
      },
      price: "",
      purchasePrice: "",
      notes: "",
      description: "",
    });

    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal
      title={t("addBook")}
      onClose={handleCancelAddBook}
      footer={
        <>
          {loadingModal ? <Loader /> : null}
          <section className="edit-user-actions">
            <CustomButton
              title={t("confirm")}
              onClick={handleCreateBook}
              icon={saveIcon}
              iconSize={16}
              disabled={loadingModal}
            />
            <CustomButton
              title={t("cancel")}
              onClick={handleCancelAddBook}
              variant="blank"
              icon={closeIcon}
              iconSize={14}
            />
          </section>
        </>
      }
    >
      <form onSubmit={handleCreateBook}>
        <div>
          {error && (
            <div className="o-row o-row--horizontal-align_center o-row--vertical-align_center">
              <ErrorBox>{errorMessage}</ErrorBox>
            </div>
          )}
          <div className="edit-user-entity">
            <span>{t("bookName")}: </span>
            <CustomInput
              name="bookName"
              value={newBookFields.bookName}
              onChange={(e) => handleNewBookInputChange(e, "bookName")}
              errorText={errorMessage ? t("inputFieldCannotBeEmpty") : ""}
            />

            <span>{t("bookType")}: </span>
            <Dropdown
              options={bookTypes}
              selected={{
                title: newBookFields.bookType.title,
                value: newBookFields.bookType.value,
              }}
              setSelected={(selected) => {
                setNewBookFields((prev) => ({
                  ...prev,
                  bookType: {
                    title: selected.title,
                    value: selected.value,
                  },
                }));
              }}
              thick
            />

            <span>{t("academicLevel")}: </span>
            <HotdropdownV2
              initOptions={[...academicYears]}
              selected={newBookFields.academicYear.title}
              setSelected={(selected) => {
                setNewBookFields((prev) => ({
                  ...prev,
                  academicYear: {
                    title: selected.title,
                    value: selected.value,
                    price: selected.price,
                  },
                }));
              }}
              page="books"
              getData={(page, limit, isDropDown, query) =>
                fetchAcademicYears(page, limit, isDropDown, query)
              }
              optionsName="classes"
              thick
              key="academic-year-dropdown"
            />

            <span>{t("singlePurchasePrice")}: </span>
            <CustomInputWithUnit
              name="purchasePrice"
              value={newBookFields.purchasePrice}
              onChange={(e) => handleNewBookInputChange(e, "purchasePrice")}
              type="number"
              step="1"
              min="0"
              unit="IQD"
            />
            
            <span>{t("quantity")}: </span>
            <CustomInput
              name="quantity"
              value={newBookFields.quantity}
              onChange={(e) => handleNewBookInputChange(e, "quantity")}
              type="number"
              min="1"
            />
            
            <span>{t("singleSellPrice")}: </span>
            <CustomInputWithUnit
              name="price"
              value={newBookFields.price}
              onChange={(e) => handleNewBookInputChange(e, "price")}
              type="number"
              step="1"
              min="0"
              unit="IQD"
            />


            <span>{t("description")}:</span>
            <CustomInput
              name="description"
              value={newBookFields.description}
              onChange={(e) => handleNewBookInputChange(e, "description")}
              type="textarea"
              flex
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default AddBookModal;
